import { Injectable, inject } from '@angular/core';
import { Observable, shareReplay, tap } from 'rxjs';
import { ApiService } from './api.service';
import { SaveBusinessUnitDto } from '@models/save-business-unit-dto.interface';
import { BusinessUnit, BusinessUnitSummary } from '@models/business-unit.interface';
import { LookupDto } from '@models/lookup-dto.interface';

@Injectable({ providedIn: 'root' })
export class BusinessUnitsService {
  private readonly apiService = inject(ApiService);
  private listCache$?: Observable<BusinessUnitSummary[]>;
  private assetClassCache$?: Observable<LookupDto[]>;
  private readonly detailCache = new Map<number, Observable<BusinessUnit>>();

  getAssetClasses(): Observable<LookupDto[]> {
    if (!this.assetClassCache$) {
      this.assetClassCache$ = this.apiService
        .get('businessunits/assetclasses')
        .pipe(shareReplay(1));
    }

    return this.assetClassCache$;
  }

  getList(forceRefresh = false): Observable<BusinessUnitSummary[]> {
    if (forceRefresh || !this.listCache$) {
      this.listCache$ = this.apiService
        .get('businessunits')
        .pipe(shareReplay(1));
    }

    return this.listCache$;
  }

  getById(id: number, forceRefresh = false): Observable<BusinessUnit> {
    if (forceRefresh || !this.detailCache.has(id)) {
      const request$ = this.apiService
        .get(`businessunits/${id}`)
        .pipe(shareReplay(1));
      this.detailCache.set(id, request$);
    }

    return this.detailCache.get(id)!;
  }

  create(dto: SaveBusinessUnitDto): Observable<BusinessUnit> {
    return this.apiService
      .post('businessunits', dto)
      .pipe(tap(saved => this.invalidateCache(saved.id)));
  }

  update(id: number, dto: SaveBusinessUnitDto): Observable<BusinessUnit> {
    return this.apiService
      .put(`businessunits/${id}`, dto)
      .pipe(tap(saved => this.invalidateCache(saved.id)));
  }

  delete(id: number): Observable<void> {
    return this.apiService
      .delete(`businessunits/${id}`)
      .pipe(tap(() => this.invalidateCache(id)));
  }

  private invalidateCache(id?: number): void {
    this.listCache$ = undefined;
    if (id !== undefined) {
      this.detailCache.delete(id);
    }
  }
}
